/**
 * WorkbenchSessionEmbed
 *
 * Embeds an active prioritization session directly inside LoopWorkbenchPage.
 * The user can run the session tool, see results, and trigger proposal lift
 * without leaving the workbench.
 *
 * Design rule: the ball holder does the work inside the ball, not outside it.
 *
 * State machine:
 *   DRAFT              → tool_picker   (no tool selected — show compact tool list)
 *   CRITERIA_FINALIZED → tool_ready    (tool selected, ready to run)
 *   RUN_COMPLETE       → results       (session done — show ranked results)
 *   PUBLISHED          → results       (published — show ranked results with lift option)
 *   default            → session_status (fallback — link to full session page)
 */

import { useState, useMemo, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpRight,
  CheckCircle2,
  Loader2,
  Play,
  Send,
  Star,
  Zap,
} from 'lucide-react';
import { useSessionStore, runSessionViaRpc } from '../../../stores/prioritization/sessionStore';
import { useItemsStore } from '../../../stores/prioritization/itemsStore';
import { usePriorityProposalStore } from '../../../stores/prioritization/priorityProposalStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { getAllAdapters, getAdapterByName } from '../../../harmonizer';
import type { SessionState } from '../../../types/prioritization/session';
import { SESSION_INTENT_LABELS } from '../../../types/prioritization/session';
import type { CreatePriorityProposalInput } from '../../../types/prioritization/priorityProposal';
import type { HarmonizedToolAdapter, Item as HarmonizerItem, ToolConfig } from '../../../harmonizer/types';
import { withCatchballQuery } from '../../../lib/catchballSessionLink';

// ─── Props ────────────────────────────────────────────────────────────────────

interface WorkbenchSessionEmbedProps {
  sessionId: string;
  cycleId: string;
  trackId: string;
  loopId: string;
  /** Called when the session completes and results are available */
  onSessionComplete?: (sessionId: string) => void;
}

// ─── State machine ────────────────────────────────────────────────────────────

type EmbedView = 'tool_picker' | 'tool_ready' | 'results' | 'session_status';

function getEmbedView(state: SessionState, selectedToolId?: string): EmbedView {
  if (state === 'RUN_COMPLETE' || state === 'PUBLISHED') return 'results';
  if (state === 'CRITERIA_FINALIZED' && selectedToolId) return 'tool_ready';
  if (state === 'DRAFT' || state === 'CRITERIA_FINALIZED') return 'tool_picker';
  return 'session_status';
}

// ─── Lift Proposal Form ───────────────────────────────────────────────────────

interface LiftFormProps {
  defaultTitle: string;
  onSubmit: (title: string, rationale: string, openQuestions: string) => Promise<void>;
  onCancel: () => void;
  isLifting: boolean;
}

function LiftForm({ defaultTitle, onSubmit, onCancel, isLifting }: LiftFormProps) {
  const [title, setTitle] = useState(defaultTitle);
  const [rationale, setRationale] = useState('');
  const [openQuestions, setOpenQuestions] = useState('');

  return (
    <div className="mt-4 border border-[var(--slate)] bg-[var(--slate-dim)] p-4 space-y-4">
      <div className="text-sm font-semibold text-[var(--text-sec)]">Lift ranked results as a proposal</div>
      <p className="text-xs text-[var(--text-sec)]">
        This creates a governance artifact that travels with the ball when you pass it up.
      </p>
      <div className="space-y-3">
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Rationale</label>
          <textarea
            value={rationale}
            onChange={(e) => setRationale(e.target.value)}
            rows={3}
            placeholder="Why does this ranking reflect the right priorities?"
            className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)]"
          />
        </div>
        <div>
          <label className="block text-xs font-medium text-[var(--text-sec)] mb-1">Open questions for Catchball</label>
          <textarea
            value={openQuestions}
            onChange={(e) => setOpenQuestions(e.target.value)}
            rows={2}
            placeholder="Any tensions or questions the tosser should be aware of?"
            className="w-full border border-[var(--rule)] bg-[var(--bg-raised)] px-3 py-2 text-sm text-[var(--text-pri)]"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={onCancel}
          className="border border-[var(--rule)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--surface)]"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={() => void onSubmit(title.trim(), rationale.trim(), openQuestions.trim())}
          disabled={isLifting || !title.trim()}
          className="inline-flex items-center gap-1.5 bg-[var(--slate)] px-3 py-1.5 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-60"
        >
          {isLifting ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
          ) : (
            <Send className="h-3.5 w-3.5" />
          )}
          Lift proposal
        </button>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export function WorkbenchSessionEmbed({
  sessionId,
  cycleId,
  trackId,
  loopId,
  onSessionComplete,
}: WorkbenchSessionEmbedProps) {
  const sessionHref = withCatchballQuery(`/prioritization/sessions/${sessionId}`, {
    loopId,
    trackId,
    cycleId,
  });
  const userId = useUserStore((s) => s.userId);
  const getSession = useSessionStore((s) => s.getSession);
  const fetchSessionById = useSessionStore((s) => s.fetchSessionById);
  const setSelectedTool = useSessionStore((s) => s.setSelectedTool);
  const fetchFromSupabase = useSessionStore((s) => s.fetchFromSupabase);

  const getItemById = useItemsStore((s) => s.getItemById);

  const createProposal = usePriorityProposalStore((s) => s.createProposal);
  const fetchForLoop = usePriorityProposalStore((s) => s.fetchForLoop);
  const proposals = usePriorityProposalStore((s) => s.proposals);

  const [isFetchingSession, setIsFetchingSession] = useState(false);
  const [isRunning, setIsRunning] = useState(false);
  const [runError, setRunError] = useState<string | null>(null);
  const [showLiftForm, setShowLiftForm] = useState(false);
  const [isLifting, setIsLifting] = useState(false);
  const [liftError, setLiftError] = useState<string | null>(null);

  const session = getSession(sessionId);

  // Load session if not yet in store
  useEffect(() => {
    if (!session) {
      setIsFetchingSession(true);
      fetchSessionById(sessionId).finally(() => setIsFetchingSession(false));
    }
  }, [session, sessionId, fetchSessionById]);

  // Proposals already attached to this session
  const sessionProposals = useMemo(
    () => proposals.filter((p) => p.sessionIds.includes(sessionId)),
    [proposals, sessionId]
  );

  // All adapters available for tool selection
  const allAdapters = useMemo(() => getAllAdapters(), []);

  // Ranking-capable adapters (arrange category or any that can rank)
  const rankingAdapters = useMemo(
    () => allAdapters.filter((a) => a.flags?.isArrangeCapable),
    [allAdapters]
  );

  // ── Handle tool selection (DRAFT → CRITERIA_FINALIZED) ──────────────────────
  const handleSelectTool = async (toolName: string) => {
    if (!session || !userId) return;
    setRunError(null);
    const result = setSelectedTool(sessionId, toolName);
    if (!result.success) {
      setRunError(result.error ?? 'Failed to select tool');
      return;
    }
    try {
      const { transitionSessionState } = useSessionStore.getState();
      await transitionSessionState(sessionId, 'CRITERIA_FINALIZED', userId);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Failed to advance session state');
    }
  };

  // ── Handle run (CRITERIA_FINALIZED → RUN_COMPLETE) ──────────────────────────
  const handleRunSession = async () => {
    if (!session || !session.selectedToolId || !userId) return;

    const adapter = getAdapterByName(session.selectedToolId);
    if (!adapter) {
      setRunError(`Tool "${session.selectedToolId}" not found in registry.`);
      return;
    }

    const itemIds = session.itemIds ?? [];
    if (itemIds.length === 0) {
      setRunError('No items in this session to rank.');
      return;
    }

    const harmonizerItems = itemIds
      .map((id) => {
        const item = getItemById(id);
        if (!item) return null;
        return { id: item.id, label: item.title, description: item.description, attributes: {} as HarmonizerItem['attributes'] };
      })
      .filter((i): i is NonNullable<typeof i> => i !== null);

    if (harmonizerItems.length === 0) {
      setRunError('Could not load item data. Try refreshing the page.');
      return;
    }

    setIsRunning(true);
    setRunError(null);

    try {
      const toolConfig = (session.toolConfig as ToolConfig) ?? {};
      const result = await adapter.runTool(harmonizerItems, null, toolConfig, null);

      const sortedScores = [...result.scores].sort(
        (a, b) => (a.rank ?? 999) - (b.rank ?? 999)
      );
      const rankings = {
        rankedItemIds: sortedScores.map((s) => s.itemId),
        scores: Object.fromEntries(
          sortedScores.map((s) => [s.itemId, { total: s.numericScore ?? 0 }])
        ),
      };

      const rpcResult = await runSessionViaRpc(sessionId, rankings);
      if (!rpcResult.success) {
        setRunError(rpcResult.error ?? 'Session run failed.');
        return;
      }

      await fetchFromSupabase();
      onSessionComplete?.(sessionId);
    } catch (err) {
      setRunError(err instanceof Error ? err.message : 'Unexpected error running session.');
    } finally {
      setIsRunning(false);
    }
  };

  // ── Handle lift proposal ─────────────────────────────────────────────────────
  const handleLiftProposal = async (title: string, rationale: string, openQuestions: string) => {
    if (!session || !userId) return;
    setIsLifting(true);
    setLiftError(null);

    const rankingOutput = session.rankingOutput;
    if (!rankingOutput) {
      setLiftError('No ranking output found. Re-run the session.');
      setIsLifting(false);
      return;
    }

    const rankedItems = rankingOutput.rankedItemIds.map((itemId, index) => {
      const item = getItemById(itemId);
      return {
        itemId,
        itemTitle: item?.title ?? itemId,
        rank: index + 1,
        score: rankingOutput.normalizedScores?.[itemId],
        sessionId,
        isStrategicallyNominated: false,
      };
    });

    const input: CreatePriorityProposalInput = {
      cycleId,
      loopId,
      trackIds: [trackId],
      sessionIds: [sessionId],
      title: title || `Ranked results — ${session.name}`,
      rationale,
      openQuestions,
      rankedItems,
      createdBy: userId,
      holderId: userId,
    };

    try {
      await createProposal(input);
      await fetchForLoop(loopId);
      setShowLiftForm(false);
    } catch (err) {
      setLiftError(err instanceof Error ? err.message : 'Lift failed.');
    } finally {
      setIsLifting(false);
    }
  };

  // ─── Loading state ────────────────────────────────────────────────────────────

  if (isFetchingSession) {
    return (
      <div className="mt-4 flex items-center gap-2 text-sm text-[var(--text-ter)]">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading session…
      </div>
    );
  }

  if (!session) {
    return (
      <div className="mt-4 text-sm text-[var(--text-ter)]">
        Session not found.{' '}
        <Link to={sessionHref} className="text-[var(--text-ter)] hover:text-[var(--text-ter)]">
          Open full session →
        </Link>
      </div>
    );
  }

  const view = getEmbedView(session.state, session.selectedToolId);
  const intentLabel = session.intent ? SESSION_INTENT_LABELS[session.intent] : session.sessionType;

  // ─── tool_picker ──────────────────────────────────────────────────────────────

  if (view === 'tool_picker') {
    const adapters = rankingAdapters.length > 0 ? rankingAdapters : allAdapters.slice(0, 5);

    return (
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-[var(--text-pri)]">
            {session.name}
            <span className="ml-2 text-xs text-[var(--text-ter)]">{intentLabel}</span>
          </div>
          <span className="inline-flex rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs font-medium text-[var(--text-ter)]">
            Draft
          </span>
        </div>
        <p className="text-xs text-[var(--text-ter)]">Select a tool to begin ranking.</p>
        <div className="grid gap-2 sm:grid-cols-2">
          {adapters.map((adapter: HarmonizedToolAdapter) => (
            <button
              key={adapter.toolName}
              type="button"
              onClick={() => void handleSelectTool(adapter.toolName)}
              className="flex items-start gap-2 border border-[var(--rule)] bg-[var(--surface)] p-3 text-left hover:border-[var(--slate)] hover:bg-[var(--slate-dim)] transition-colors"
            >
              <Zap className="mt-0.5 h-4 w-4 flex-shrink-0 text-[var(--text-ter)]" />
              <div>
                <div className="text-sm font-medium text-[var(--text-pri)]">{adapter.toolName}</div>
                <div className="text-xs text-[var(--text-ter)] capitalize">{adapter.category}</div>
              </div>
            </button>
          ))}
        </div>
        {adapters.length === 0 && (
          <p className="text-xs text-[var(--text-ter)]">No tools available. Check harmonizer registry.</p>
        )}
        {runError && <p className="text-xs text-[var(--coral)]">{runError}</p>}
        <Link
          to={sessionHref}
          className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]"
        >
          <ArrowUpRight className="h-3 w-3" />
          Open full session
        </Link>
      </div>
    );
  }

  // ─── tool_ready ───────────────────────────────────────────────────────────────

  if (view === 'tool_ready') {
    const adapter = session.selectedToolId ? getAdapterByName(session.selectedToolId) : null;
    const itemCount = session.itemIds?.length ?? 0;

    return (
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="text-sm font-medium text-[var(--text-pri)]">
            {session.name}
            <span className="ml-2 text-xs text-[var(--text-ter)]">{intentLabel}</span>
          </div>
          <span className="inline-flex rounded-full bg-[var(--slate-dim)] px-2 py-0.5 text-xs font-medium text-[var(--text-sec)]">
            Ready to run
          </span>
        </div>
        <div className="border border-[var(--rule)] bg-[var(--surface)] p-3">
          <div className="flex items-center gap-2">
            <Zap className="h-4 w-4 text-[var(--text-ter)]" />
            <span className="text-sm font-medium text-[var(--text-pri)]">
              {adapter?.toolName ?? session.selectedToolId}
            </span>
          </div>
          <p className="mt-1 text-xs text-[var(--text-ter)]">
            {itemCount} item{itemCount !== 1 ? 's' : ''} · {adapter?.category ?? 'tool'}
          </p>
        </div>
        {runError && <p className="text-xs text-[var(--coral)]">{runError}</p>}
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => void handleRunSession()}
            disabled={isRunning}
            className="inline-flex items-center gap-2 bg-[var(--slate)] px-3 py-2 text-sm font-medium text-white hover:bg-[var(--slate)] disabled:opacity-60"
          >
            {isRunning ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Play className="h-4 w-4" />
            )}
            {isRunning ? 'Running…' : 'Run session'}
          </button>
          <Link
            to={sessionHref}
            className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]"
          >
            <ArrowUpRight className="h-3 w-3" />
            Open full session
          </Link>
        </div>
      </div>
    );
  }

  // ─── results ──────────────────────────────────────────────────────────────────

  if (view === 'results') {
    const rankingOutput = session.rankingOutput;
    const rankedItemIds = rankingOutput?.rankedItemIds ?? [];
    const alreadyLifted = sessionProposals.length > 0;

    return (
      <div className="mt-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-[var(--green)]" />
            <span className="text-sm font-medium text-[var(--text-pri)]">{session.name}</span>
            <span className="text-xs text-[var(--text-ter)]">{intentLabel}</span>
          </div>
          <span className="inline-flex rounded-full bg-[var(--green-dim)] px-2 py-0.5 text-xs font-medium text-[var(--green)] dark:text-[var(--text-ter)]">
            {session.state === 'PUBLISHED' ? 'Published' : 'Complete'}
          </span>
        </div>

        {rankedItemIds.length > 0 ? (
          <div className="border border-[var(--rule)] bg-[var(--bg-raised)] p-3">
            <div className="text-xs font-medium text-[var(--text-ter)] mb-2">Ranked results</div>
            <ol className="space-y-1.5">
              {rankedItemIds.slice(0, 8).map((itemId, index) => {
                const item = getItemById(itemId);
                const score = rankingOutput?.normalizedScores?.[itemId];
                return (
                  <li key={itemId} className="flex items-center gap-2 text-xs">
                    <span className="w-5 text-center font-bold text-[var(--text-ter)]">{index + 1}</span>
                    <span className="flex-1 text-[var(--text-pri)]">{item?.title ?? itemId}</span>
                    {score != null && (
                      <span className="text-[var(--text-ter)] tabular-nums">{Math.round(score)}</span>
                    )}
                  </li>
                );
              })}
              {rankedItemIds.length > 8 && (
                <li className="pl-7 text-xs text-[var(--text-ter)]">
                  + {rankedItemIds.length - 8} more
                </li>
              )}
            </ol>
          </div>
        ) : (
          <p className="text-xs text-[var(--text-ter)]">No ranking output available.</p>
        )}

        {/* Already-lifted proposals for this session */}
        {alreadyLifted && (
          <div className="flex items-center gap-2 bg-[var(--slate-dim)] border border-[var(--slate)] px-3 py-2">
            <Star className="h-4 w-4 text-[var(--text-ter)] flex-shrink-0" />
            <span className="text-xs text-[var(--text-sec)]">
              {sessionProposals.length} proposal{sessionProposals.length !== 1 ? 's' : ''} lifted
            </span>
          </div>
        )}

        {liftError && <p className="text-xs text-[var(--coral)]">{liftError}</p>}

        {!alreadyLifted && !showLiftForm && rankedItemIds.length > 0 && (
          <button
            type="button"
            onClick={() => setShowLiftForm(true)}
            className="inline-flex items-center gap-1.5 border border-[var(--slate)] bg-[var(--slate-dim)] px-3 py-1.5 text-sm font-medium text-[var(--text-sec)] hover:bg-[var(--slate-dim)]"
          >
            <Send className="h-3.5 w-3.5" />
            Lift proposal
          </button>
        )}

        {showLiftForm && (
          <LiftForm
            defaultTitle={`Ranked results — ${session.name}`}
            onSubmit={handleLiftProposal}
            onCancel={() => setShowLiftForm(false)}
            isLifting={isLifting}
          />
        )}

        <Link
          to={sessionHref}
          className="inline-flex items-center gap-1 text-xs text-[var(--text-ter)] hover:text-[var(--text-ter)]"
        >
          <ArrowUpRight className="h-3 w-3" />
          Open full session
        </Link>
      </div>
    );
  }

  // ─── session_status (fallback) ────────────────────────────────────────────────

  return (
    <div className="mt-4 space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-sm font-medium text-[var(--text-pri)]">{session.name}</span>
        <span className="inline-flex rounded-full bg-[var(--hover-tint)] px-2 py-0.5 text-xs font-medium text-[var(--text-ter)] capitalize">
          {session.state.toLowerCase().replace('_', ' ')}
        </span>
      </div>
      <Link
        to={sessionHref}
        className="inline-flex items-center gap-1 text-sm font-medium text-[var(--text-ter)] hover:text-[var(--text-ter)]"
      >
        <ArrowUpRight className="h-4 w-4" />
        Open full session
      </Link>
    </div>
  );
}

export default WorkbenchSessionEmbed;
